import {
  Component,
  ViewChild,
  EventEmitter,
  Output,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
} from '@angular/core';

import { ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { NgbAccordionDirective } from '@ng-bootstrap/ng-bootstrap';

import find from 'lodash/find';
import map from 'lodash/map';
import extend from 'lodash/extend';

import {
  ngHelper,
  OUTLET_SCHEDULE_TYPES,
  UiContextService,
  IOutletDelivery,
  IScheduleOverride,
} from '@cakeiteasy/web-utils';

import {
  ICartDailyStockSoldOutProductType,
  ICartDeliveryInfoOutlet,
} from '../../../cart/models/delivery/i-cart-delivery-info';
import { OutletScheduleOverridesFacadeService } from '../../services/outlet-schedule-overrides-facade.service';

@Component({
  standalone: false,
  selector: 'choose-outlet',
  templateUrl: './choose-outlet.component.html',
  styleUrls: ['./choose-outlet.component.scss'],
})
export class ChooseOutletComponent implements OnInit, OnChanges {
  @ViewChild('accordion', { static: true }) public accordion: NgbAccordionDirective;

  @Input() bakeryId: number;
  @Input() outlets: any[];
  @Input() unavailableOutlets: any[];
  @Input() hasDeliveryToDoor: boolean;
  @Input() showTitle = true;
  @Input() selectedOutlet: any;
  @Input() outletFilter: string;
  @Input() containerRef: string | number | ElementRef | HTMLElement = null;
  @Input() scheduleType: string = OUTLET_SCHEDULE_TYPES.PICKUP_SCHEDULE;
  /**Translation key for the confirm button — which step it says it leads to.*/
  @Input() nextBtnTextKey = 'orderProcess.delivery.goToPayment';
  @Input() nextBtnView = 'btn-pink';
  @Input() fullCapacityDays: { begin: string; end: string }[];
  @Input() availabilityInfoOutlets: ICartDeliveryInfoOutlet[];
  @Input() dailyStockSoldOutProductTypes: ICartDailyStockSoldOutProductType[] = [];
  @Input() orderId: number;
  @Input() isAsapAvailable: boolean;

  @Input() isWaitUntilOutletSelect: boolean = false;
  @Input() isOutletSelected: { isOutletSelected: boolean } = { isOutletSelected: false };

  @Output() onNext = new EventEmitter<void>();
  @Output() deliveryToDoorRequested = new EventEmitter<void>();
  @Output() storesMapRequested = new EventEmitter<void>();
  @Output() onMapRequested = new EventEmitter<IOutletDelivery>();
  @Output() onOutletChange = new EventEmitter<{ outletId: number; date: Date; timeRange: string }>();
  @Output() onAsapSelected = new EventEmitter<{ outletId: number; date: Date; timeRange: string }>();
  @Output() onFilterChange = new EventEmitter<string>();

  public search = '';
  public panelIdPrefix = 'outlet_';

  public availabilityByOutletId = new Map<number, ICartDeliveryInfoOutlet>();

  constructor(
    private _scrollToService: ScrollToService,
    private _uiContext: UiContextService,
    private _elRef: ElementRef,
    public outletScheduleOverrides: OutletScheduleOverridesFacadeService,
  ) {}

  ngOnInit() {
    this._init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'selectedOutlet', (selectedOutlet) => {
      if (!selectedOutlet) {
        return;
      }

      const panelId = this.panelIdPrefix + selectedOutlet.id;

      if (this.accordion.isExpanded(panelId)) {
        return;
      }

      setTimeout(() => {
        this.accordion.toggle(panelId);
      }, 10);
    });

    ngHelper.ifChanged(changes, 'unavailableOutlets', (unavailableOutlets) => {
      if (unavailableOutlets) {
        this.outlets = this._mapUnavailableOutlet(this.outlets, unavailableOutlets);
      }
    });

    ngHelper.ifChanged(changes, 'availabilityInfoOutlets', (availabilityInfoOutlets) => {
      this.availabilityByOutletId.clear();
      availabilityInfoOutlets.forEach((outlet) => {
        this.availabilityByOutletId.set(outlet.id, outlet);
      });
    });
  }

  public onOutletOpened(outletId: number) {
    this.selectedOutlet = find(this.outlets, { id: outletId });

    if (this.selectedOutlet) {
      this.outletScheduleOverrides.ensure(this.bakeryId, this.selectedOutlet.id);

      this.onOutletChange.emit({
        outletId: this.selectedOutlet.id,
        date: this.selectedOutlet.date,
        timeRange: this.selectedOutlet.timeRange,
      });
    }

    if (this._uiContext.isMobile()) {
      setTimeout(() => {
        this._scrollToService.scrollTo({
          offset: -10,
          target: this._elRef.nativeElement.querySelector(`#${this.panelIdPrefix + outletId}`),
        });
      });
    }
  }

  private _init() {
    this.search = this.outletFilter;
  }

  private _mapUnavailableOutlet(outlets, unavailableOutlets) {
    return map(outlets, (o) => {
      const uOutlet = find(unavailableOutlets, ['id', o.id]);
      if (uOutlet) {
        const unavailableProductTypes = map(uOutlet.unavailableProductTypes, 'name').join(', ');
        return extend(o, { unavailableProductTypes });
      } else {
        return extend(o, { unavailableProductTypes: '' });
      }
    });
  }
}
